import apiClient from './apiClient';

/**
 * ═══════════════════════════════════════════════════════════════════════
 * Visits Export API — Excel download + queue print-summary
 *
 * Legacy Excel export (ADMIN-only, EXPORT_VISIT-gated on the backend):
 *   GET /api/organisation/visits/export?date=YYYY-MM-DD&format=standard
 *   GET /api/organisation/visits/export?date=YYYY-MM-DD&format=busy
 *
 * Both return application/vnd.openxmlformats-officedocument.spreadsheetml.sheet
 *
 * Standard format columns:
 *   Receipt No., Date, Patient Name, Phone, Age, Gender, Doctor,
 *   Payment Mode, Fee (₹), Queue No., Visit Type, Status
 *   (S8/EP-5: LAB-scope rows additionally gain Source (OPD/IPD), Settlement
 *   status, Settlement date — server-driven; honors the optional `type` /
 *   `settlement` query params below.)
 *
 * Busy format columns (Busy software import-ready):
 *   Voucher No., Date, Party Name, Amount, Payment Mode, Narration
 *
 * Queue print-summary (TOKEN-UPDATE/T2, EXPORT_VISIT-gated for ADMIN /
 * RECEPTIONIST / DOCTOR, doctor self-scoped server-side):
 *   GET /api/organisation/visits/print-summary?date=YYYY-MM-DD
 *   → { date, visits: { [visit_id]: { amount_collected_rupees, lab_tests,
 *       refunded_rupees, net_rupees, cancelled, refunds: [{ receipt_number,
 *       amount_rupees }] } }, day_totals: { collected_rupees, refunded_rupees,
 *       net_rupees } }
 *   (Issue-2: `refunded_rupees`/`net_rupees`/`cancelled`/`refunds`/
 *   `day_totals` are NEW — additive; `amount_collected_rupees` stays GROSS,
 *   unchanged. `day_totals` covers the server-scoped day and is NOT used by
 *   `printQueueSheet` — the Day Summary footer sums the printed rows only.)
 *   BUSY-VOUCHER R1 (OR-41): each LAB-visit / IPD-lab entry additionally
 *   carries `lab_sections: [{ section: 'LAB'|'RAD', lab_tests, amount_collected_rupees,
 *   refunded_rupees, net_rupees, refunds }]` — LAB then RAD, only sections with
 *   tests or money; Σ slices == the entry's top-level money. The server decides
 *   the section from each test's Type. Absent on OPD/OTC entries and on an old
 *   backend (the sheet then prints the whole visit under Lab).
 *   Feeds `printQueueSheet` (src/utils/printQueueSheet.js) — one fetch per
 *   print click, joined client-side against the already-filtered visit list.
 * ═══════════════════════════════════════════════════════════════════════
 */

/**
 * Download daily visits as Excel.
 *
 * @param {string} date         - ISO date string YYYY-MM-DD
 * @param {'standard'|'busy'}   - Export format
 * @param {string} [doctorId]   - Optional doctor filter
 * @param {{type?: 'IPD'|'OPD', settlement?: 'SETTLED'|'UNSETTLED'}} [labFilters]
 *   S8 (EP-5) — Lab Visit tab Type/Settlement lens; only meaningful in LAB
 *   scope (caller resets both to '' outside it). Server adds Source/
 *   Settlement-status/Settlement-date columns and honors these filters.
 */
export const downloadVisitsExcel = async (date, format = 'standard', doctorId, labFilters = {}) => {
  try {
    const params = { date, format };
    if (doctorId) params.doctor_id = doctorId;
    if (labFilters.type) params.type = labFilters.type;
    if (labFilters.settlement) params.settlement = labFilters.settlement;

    const response = await apiClient.get('/api/organisation/visits/export', {
      params,
      responseType: 'blob',
    });

    const blob = new Blob([response.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const blobUrl = URL.createObjectURL(blob);

    const formatLabel = format === 'busy' ? 'busy' : 'opd';
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = `visits_${formatLabel}_${date}.xlsx`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
  } catch (error) {
    console.error('[VisitsExportAPI] Error downloading export:', error);
    const msg = error.response?.data?.detail || error.message || 'Failed to export visits';
    throw new Error(msg);
  }
};

/**
 * Fetch the queue print-summary for a given day (amount collected + lab
 * tests per visit, keyed by visit_id, plus Issue-2 refund/net/cancelled
 * fields). Read-only, additive endpoint.
 *
 * @param {string} date - ISO date string YYYY-MM-DD
 * @returns {Promise<{date: string, visits: Record<string, {
 *   amount_collected_rupees: number, lab_tests: string[],
 *   refunded_rupees: number, net_rupees: number, cancelled: boolean,
 *   refunds: Array<{receipt_number: number|null, amount_rupees: number}>,
 *   lab_sections?: Array<{section: 'LAB'|'RAD', lab_tests: string[],
 *     amount_collected_rupees: number|null, refunded_rupees: number|null,
 *     net_rupees: number|null, refunds: Array<{receipt_number: number|null,
 *     receipt_series: string|null, amount_rupees: number}>}>,
 * }>, day_totals: {collected_rupees: number, refunded_rupees: number, net_rupees: number}}>}
 */
export const fetchPrintSummary = async (date) => {
  try {
    const response = await apiClient.get('/api/organisation/visits/print-summary', {
      params: { date },
    });
    return response.data;
  } catch (error) {
    console.error('[VisitsExportAPI] Error fetching print summary:', error);
    // print-summary errors are machine-readable envelopes: detail = { code,
    // message } (RC9). Surface the human `message`; fall back to a string
    // detail (legacy) or the axios message.
    const detail = error.response?.data?.detail;
    const msg = (detail && typeof detail === 'object' ? detail.message : detail)
      || error.message || 'Failed to fetch print summary';
    throw new Error(msg);
  }
};
